/**
 * Centralized window-level keyboard hotkeys for AutoCAD drawing-aid
 * sysvars. All F-key toggles and Shift-based temporary overrides mutate a
 * single zustand store so any consumer (status bar, command feed, DI
 * overlay) reacts to the same source of truth. Mounted once via
 * `useSysvarHotkeys()` from `App()` — the always-rendered root (App.tsx);
 * an earlier iteration lived inline in the since-removed
 * PrimitiveAtlasStage.tsx.
 *
 * Focus guard: key events fired while focus is inside an editable
 * target (input, textarea, select, contenteditable) are ignored so the
 * user can type "F8" inside a filename field without toggling Ortho.
 * Mirrors the guard helper used by useCommandLine.ts.
 *
 * Handlers are extracted as standalone functions (`handleSysvarKeyDown`
 * / `handleSysvarKeyUp`) so tests can drive them without mounting a
 * React tree.
 *
 * ### Temporary Overrides (Phase 3 — AutoCAD parity)
 *
 * Standard Shift key:
 *   Shift (hold) — temp toggle ORTHO for the duration of the press.
 *     If ORTHO is off: temp-on. If ORTHO is on: temp-off.
 *   Shift release — clears ALL temp overrides.
 *
 * Shift+F8:
 *   Permanent ORTHO toggle (NOT temp). AutoCAD parity: Shift+F8 is the
 *   permanent toggle; bare Shift is the temporary one.
 *
 * Shift+letter temp OSNAP single-mode keys (while held):
 *   Shift+E  → Endpoint only  (bit 1)
 *   Shift+M  → Midpoint only  (bit 2)
 *   Shift+C  → Center only    (bit 4)
 *   Shift+Q  → Quadrant only  (bit 16)
 *   Shift+P  → Perpendicular  (bit 128)
 *   Shift+T  → Tangent        (bit 256)
 *   Shift+D  → Disable OSNAP temporarily (osnapMode=0)
 *   Shift+A  → All osnap modes temporarily (restore effective OSMODE)
 *
 * On keyup of ANY Shift+letter temp-override key, the osnapMode temp
 * override is cleared (restoring the base OSMODE). The ortho temp
 * override set by bare Shift is cleared on Shift keyup.
 *
 * Note: TEMPOVERRIDES sysvar must be 1 (default) for these to activate.
 */

import { useEffect } from "react";

import { useSysvarStore, POLARMODE_TRACKING_ON_BIT } from "./sysvarStore";

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/**
 * OSMODE single-mode bits for each temp-override Shift+letter key.
 * AutoCAD standard OSMODE bit encoding.
 */
const SHIFT_OSNAP_KEYS: Record<string, number> = {
  E: 1,    // Endpoint
  M: 2,    // Midpoint
  C: 4,    // Center
  Q: 16,   // Quadrant
  P: 128,  // Perpendicular
  T: 256,  // Tangent
};

/**
 * #355 @codex — the Shift+letter OSNAP override keys CURRENTLY held (E/M/C/Q/P/T/
 * D/A), in PRESS ORDER (most-recently-pressed last). A keydown pushes; a keyup
 * splices. Order matters: AutoCAD's last-pressed held override wins, so on a
 * keyup with others still held we RECOMPUTE the active OSNAP from the last
 * remaining key rather than leaving the released key's mode stuck. The bare-Shift
 * temp-ORTHO (which an override keydown clears so the override doesn't constrain
 * drafting) is restored on a letter keyup ONLY when this list becomes empty —
 * otherwise a key ROLLOVER (Shift+E held, then M down, then E up while M still
 * held) would wrongly re-enable ORTHO mid-override. A keyup for a key NOT in this
 * list (e.g. Ctrl+Shift+A, whose keydown the modifier guard ignored) is a no-op
 * so it never spuriously applies a bare-Shift override. Module-level because the
 * pure keydown/keyup handlers carry no cross-call state; cleared on Shift keyup.
 */
const heldOverrideKeys: string[] = [];

/** OSNAP temp-override mode for an override key: D disables, A restores all. */
function overrideOsnapMode(upper: string): number | null {
  if (upper === "D") return 0; // disable all OSNAP temporarily
  if (upper === "A") return null; // restore effective OSMODE (no override)
  return SHIFT_OSNAP_KEYS[upper] ?? null;
}

/** Record an override key as held, most-recently-pressed last (dedupe re-press). */
function trackOverrideKey(upper: string): void {
  const i = heldOverrideKeys.indexOf(upper);
  if (i !== -1) heldOverrideKeys.splice(i, 1);
  heldOverrideKeys.push(upper);
}

/** Test-only: clear the held-override-key tracking between cases (#355). */
export function __resetHeldOverrideKeysForTest(): void {
  heldOverrideKeys.length = 0;
}

/**
 * Codex P2 (grips round-6) — true when this keydown IS (bare Shift) or
 * BEGINS (Shift+E/M/C/Q/P/T/D/A) a temp-override hotkey this module owns.
 * Bare Shift must count too: the physical key sequence for e.g. Shift+E is
 * keydown(Shift) THEN keydown(E, shiftKey=true), so a caller that reacts to
 * "any other key" (StageGripDrag's armed-grip-batch abort guard — grip
 * dragging consumes these SAME temp overrides live via `resolveGripSnap`'s
 * `osnapBindingsRef`) must not act on the leading bare-Shift keydown either,
 * or the second (letter) keydown never has a live gesture left to reach.
 * Exported as the single source of truth for "is this key one of mine" so
 * other window-level keydown listeners don't duplicate/drift from this list.
 */
export function isSysvarOverrideHotkey(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (e.key === "Shift") return true;
  if (!e.shiftKey) return false;
  const upper = e.key.toUpperCase();
  return upper === "D" || upper === "A" || upper in SHIFT_OSNAP_KEYS;
}

/**
 * Pure keydown handler driving sysvarStore mutations. Exposed for unit
 * tests; the production code path runs this via the event listener
 * installed by `useSysvarHotkeys`.
 */
export function handleSysvarKeyDown(e: KeyboardEvent): void {
  if (isEditableTarget(e.target)) return;
  // #349 P2 — drawing-aid hotkeys are modifier-free (Shift-only for the temp
  // overrides). When Ctrl/Meta/Alt is held the keystroke belongs to a global
  // shortcut (e.g. Ctrl+Shift+A "restore previous selection") — do NOT intercept
  // / preventDefault it here; let it fall through to useKeyboardShortcuts.
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  // #351 P2 — OS key auto-repeat (`e.repeat`) for a HELD recognized hotkey must
  // still be CONSUMED (`preventDefault`) so the held key never leaks its default
  // (a non-default-prevented shell char gets injected into the command line —
  // useKeyboardShortcuts.ts), but must NOT re-run a non-idempotent TOGGLE. So
  // each F-key / Shift+F8 toggle below guards `if (e.repeat) return` AFTER its
  // preventDefault; the Shift+letter/D/A overrides are idempotent (re-set the
  // same value) so they safely run on repeat and stay default-prevented.
  const store = useSysvarStore.getState();

  // ── Shift+F8: permanent ORTHO toggle (NOT a temp override) ──
  if (e.shiftKey && e.key === "F8") {
    e.preventDefault();
    if (e.repeat) return; // #351 — consume the held key, toggle once per press
    // Clear any existing ortho temp override then toggle the permanent value
    store.setTempOverride({ ortho: null });
    store.setVar("ORTHOMODE", store.values.ORTHOMODE ? 0 : 1);
    return;
  }

  // ── Shift+D: temp disable all OSNAP ──
  // #349 P2 — also clear the ortho temp override: the bare-Shift keydown that
  // precedes this letter keydown flipped ORTHO; a Shift+letter OSNAP override
  // must NOT leave ORTHO toggled while held (it would constrain drafting input).
  if (e.shiftKey && e.key === "D" && store.values.TEMPOVERRIDES === 1) {
    e.preventDefault();
    trackOverrideKey("D"); // #355 — track (ordered) for the keyup recompute/restore
    store.setTempOverride({ osnapMode: 0, ortho: null });
    return;
  }

  // ── Shift+A: temp restore all OSNAP (no override) ──
  if (e.shiftKey && e.key === "A" && store.values.TEMPOVERRIDES === 1) {
    e.preventDefault();
    trackOverrideKey("A"); // #355
    store.setTempOverride({ osnapMode: null, ortho: null });
    return;
  }

  // ── Shift+letter: temp single-mode OSNAP ──
  if (e.shiftKey && store.values.TEMPOVERRIDES === 1) {
    const upper = e.key.toUpperCase();
    if (upper in SHIFT_OSNAP_KEYS) {
      e.preventDefault();
      trackOverrideKey(upper); // #355 — track (ordered) for the keyup recompute/restore
      // #349 P2 — clear the bare-Shift ORTHO flip when the letter override engages.
      store.setTempOverride({ osnapMode: SHIFT_OSNAP_KEYS[upper], ortho: null });
      return;
    }
  }

  switch (e.key) {
    // #351 — every F-key consumes the held key (preventDefault) but toggles only
    // once per physical press (`if (e.repeat) return` after preventDefault).
    case "F7": {
      e.preventDefault();
      if (e.repeat) return;
      store.setVar("GRIDMODE", store.values.GRIDMODE ? 0 : 1);
      return;
    }
    case "F8": {
      e.preventDefault();
      if (e.repeat) return;
      // Bare F8 (no Shift): permanent ORTHO toggle
      store.setVar("ORTHOMODE", store.values.ORTHOMODE ? 0 : 1);
      return;
    }
    case "F9": {
      e.preventDefault();
      if (e.repeat) return;
      store.setVar("SNAPMODE", store.values.SNAPMODE ? 0 : 1);
      return;
    }
    case "F10": {
      e.preventDefault();
      if (e.repeat) return;
      // Toggle ONLY the master bit 0; preserve the config bits (POLARADDANG etc.)
      // so F10 never drops the additional-angle setup, and turns polar ON even
      // when only a config bit was previously set (#492 @codex P2).
      const polar = store.values.POLARMODE;
      store.setVar(
        "POLARMODE",
        polar & POLARMODE_TRACKING_ON_BIT
          ? polar & ~POLARMODE_TRACKING_ON_BIT
          : polar | POLARMODE_TRACKING_ON_BIT,
      );
      return;
    }
    case "F11": {
      e.preventDefault();
      if (e.repeat) return;
      // OTRACK master toggle (OTRACKMODE) — mirrors the F8/F10 pattern.
      store.setVar("OTRACKMODE", store.values.OTRACKMODE ? 0 : 1);
      return;
    }
    case "F12": {
      e.preventDefault();
      if (e.repeat) return;
      // Toggle bit 1 (pointer input) of DYNMODE. AutoCAD parity:
      // F12 targets pointer only; dim-input bit is independent.
      store.setVar("DYNMODE", store.values.DYNMODE ^ 1);
      return;
    }
    case "Shift": {
      if (store.values.TEMPOVERRIDES !== 1) return;
      // Bare Shift (no other key yet): temp flip ORTHO for the duration
      // of the press. If ORTHO is off → temp-on; if on → temp-off.
      store.setTempOverride({ ortho: !(store.values.ORTHOMODE !== 0) });
      return;
    }
  }
}

/**
 * Pure keyup handler — clears the transient Shift-based temp overrides.
 *
 * - Shift key up: clear ortho temp override (from bare Shift press).
 * - Shift+letter key up: clear osnapMode temp override.
 *   We detect this by checking shiftKey is still true on the keyup event
 *   for letter keys (shiftKey remains true until Shift itself is released).
 */
export function handleSysvarKeyUp(e: KeyboardEvent): void {
  if (e.key === "Shift") {
    // Shift released: ALL Shift-based temp overrides end. Clear the held-key
    // list and both overrides.
    heldOverrideKeys.length = 0;
    useSysvarStore.getState().setTempOverride({ ortho: null, osnapMode: null });
    return;
  }

  // A Shift+letter override key was released while Shift is still held.
  if (e.shiftKey) {
    const upper = e.key.toUpperCase();
    const idx = heldOverrideKeys.indexOf(upper);
    // #355 @codex (b) — only act on keyups for keys we actually TRACKED. A
    // letter whose keydown the modifier guard ignored (e.g. Ctrl+Shift+A, which
    // falls through to global shortcuts) is not in the list; without this guard
    // the empty-list branch below would spuriously apply a bare-Shift ORTHO.
    if (idx === -1) return;
    heldOverrideKeys.splice(idx, 1);
    const store = useSysvarStore.getState();
    if (heldOverrideKeys.length > 0) {
      // #355 @codex (a) — another override is still held. RECOMPUTE the active
      // OSNAP from the last-pressed remaining key (AutoCAD: last override wins)
      // rather than leaving the released key's mode stuck. ORTHO stays cleared
      // while any override is held (it would otherwise constrain drafting).
      const active = heldOverrideKeys[heldOverrideKeys.length - 1];
      store.setTempOverride({ osnapMode: overrideOsnapMode(active), ortho: null });
    } else {
      // #351 round-2 — last override released: clear it AND restore the
      // bare-Shift temp-ORTHO so continuing to hold Shift keeps the documented
      // temporary ORTHO. Mirrors the bare-Shift keydown value; TEMPOVERRIDES-gated.
      store.setTempOverride({
        osnapMode: null,
        ortho:
          store.values.TEMPOVERRIDES === 1
            ? !(store.values.ORTHOMODE !== 0)
            : null,
      });
    }
  }
}

/**
 * React hook — mounted once via `useSysvarHotkeys()` from `App()` (the
 * always-rendered root). The listener is attached to `window` so it works
 * regardless of which child has focus, and is torn down on unmount.
 */
export function useSysvarHotkeys(): void {
  useEffect(() => {
    window.addEventListener("keydown", handleSysvarKeyDown);
    window.addEventListener("keyup", handleSysvarKeyUp);
    return () => {
      window.removeEventListener("keydown", handleSysvarKeyDown);
      window.removeEventListener("keyup", handleSysvarKeyUp);
    };
  }, []);
}

/**
 * AutoCAD OSMODE "suppress all running osnaps" sentinel (bit 0x4000 = 16384).
 *
 * When set in OSMODE, AutoCAD remembers the selected running-snap bits but
 * turns the running object snap OFF (the F3 / status-bar OSNAP toggle persists
 * this bit so the chosen modes survive a toggle-off/on cycle). The live snap
 * resolver must therefore see ZERO active running modes while it is set.
 *
 * `OSMODE_BITS` in `resolveObjectSnap.ts` intentionally omits this bit, so
 * `decodeOsmode` neither maps nor strips it — leaving the running-snap bits in
 * a suppressed OSMODE (e.g. 20583 = 16384 + 4199) to decode to a NON-empty
 * active set. `selectEffectiveOsmode` is the single live-path chokepoint that
 * enforces the sentinel (the DSettings Object-Snap tab handles it separately
 * for its own checkbox UI). Explicit one-shot overrides (Shift+letter temp
 * overrides) are NOT suppressed — they flow through `tempOverride.osnapMode`,
 * which bypasses this mask, matching AutoCAD (explicit overrides work even when
 * running OSNAP is toggled off).
 */
export const OSMODE_SUPPRESS_ALL = 0x4000;

/**
 * Derived selector: effective OSNAP mode considering temp overrides.
 * Geometry code should read this instead of OSMODE directly.
 *
 * - If an osnapMode temp override is set → use that bitmask (exempt from the
 *   suppress-all sentinel — explicit overrides always work).
 * - Else if OSMODE has the suppress-all bit (16384) → 0 (no running snaps).
 * - Otherwise → use the OSMODE sysvar value.
 */
export function selectEffectiveOsmode(s: {
  values: { OSMODE: number };
  tempOverride: { osnapMode: number | null };
}): number {
  if (s.tempOverride.osnapMode !== null) return s.tempOverride.osnapMode;
  if ((s.values.OSMODE & OSMODE_SUPPRESS_ALL) !== 0) return 0;
  return s.values.OSMODE;
}
